import {
  Checkbox,
  Chip,
  Typography,
  Box,
  Paper,
  Grid,
  makeStyles,
} from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { Env, ServiceEntry, resolveDepOrder } from '../promoteUtils';

const useStyles = makeStyles((theme) => ({
  row: {
    display: 'grid',
    gridTemplateColumns: '32px 1fr auto auto',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    padding: theme.spacing(1.5, 2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    marginBottom: theme.spacing(1),
    cursor: 'pointer',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  rowActive: {
    borderColor: theme.palette.primary.main,
    background:
      theme.palette.type === 'dark'
        ? 'rgba(139, 92, 246, 0.08)'
        : 'rgba(139, 92, 246, 0.04)',
  },
  serviceName: {
    fontFamily: 'monospace',
    fontWeight: 600,
    fontSize: 13,
  },
  versionChip: {
    fontFamily: 'monospace',
    fontSize: 11,
  },
  versionArrow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: 11,
  },
  rail: {
    padding: theme.spacing(2),
  },
  railLabel: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  envPathRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
    fontSize: 12,
  },
  envDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    background: theme.palette.divider,
  },
  envDotActive: {
    background: theme.palette.primary.main,
  },
  envName: {
    fontFamily: 'monospace',
    fontWeight: 600,
  },
  envSub: {
    fontSize: 10,
    color: theme.palette.text.secondary,
  },
  depItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontSize: 12,
    padding: theme.spacing(0.25, 0),
  },
  depIndex: {
    fontFamily: 'monospace',
    fontSize: 11,
    color: theme.palette.text.secondary,
    width: 18,
  },
}));

export interface VersionMap {
  [service: string]: Partial<Record<Env, string>>;
}

export interface StepSelectProps {
  services: ServiceEntry[];
  versions: VersionMap;
  selectedNames: Set<string>;
  setSelectedNames: (next: Set<string>) => void;
  sourceEnv: Env;
  targetEnv: Env;
}

export const StepSelect = ({
  services,
  versions,
  selectedNames,
  setSelectedNames,
  sourceEnv,
  targetEnv,
}: StepSelectProps) => {
  const classes = useStyles();

  const toggle = (name: string) => {
    const next = new Set(selectedNames);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    setSelectedNames(next);
  };

  const order = resolveDepOrder(services, selectedNames);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={8}>
        <Paper variant="outlined" style={{ padding: 16 }}>
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            Services in this release
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Toggle services to include. Versions shown are the current head in each environment.
          </Typography>
          <Box mt={2}>
            {services.map((s) => {
              const on = selectedNames.has(s.name);
              const sourceV = versions[s.name]?.[sourceEnv];
              const targetV = versions[s.name]?.[targetEnv];
              const promotes = !!sourceV && !!targetV && sourceV !== targetV;
              return (
                <div
                  key={s.name}
                  className={`${classes.row} ${on ? classes.rowActive : ''}`}
                  onClick={() => toggle(s.name)}
                >
                  <Checkbox
                    checked={on}
                    color="primary"
                    size="small"
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggle(s.name)}
                  />
                  <div>
                    <div className={classes.serviceName}>{s.name}</div>
                    <Typography variant="caption" color="textSecondary">
                      {s.entity.spec?.owner ? String(s.entity.spec.owner) : ''}
                    </Typography>
                  </div>
                  <div className={classes.versionArrow}>
                    <span>{targetV ?? '—'}</span>
                    <ArrowForwardIcon style={{ fontSize: 14 }} />
                    <span style={{ fontWeight: 600 }}>{sourceV ?? '—'}</span>
                  </div>
                  <Chip
                    size="small"
                    variant={promotes ? 'default' : 'outlined'}
                    color={promotes ? 'primary' : 'default'}
                    label={promotes ? 'new version' : 'up to date'}
                  />
                </div>
              );
            })}
          </Box>
        </Paper>
      </Grid>

      <Grid item xs={12} md={4}>
        <Paper variant="outlined" className={classes.rail}>
          <div className={classes.railLabel}>Promote</div>
          <EnvPath sourceEnv={sourceEnv} targetEnv={targetEnv} classes={classes} />
        </Paper>

        <Box mt={2}>
          <Paper variant="outlined" className={classes.rail}>
            <div className={classes.railLabel}>Deploy order</div>
            {order.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No services selected.
              </Typography>
            ) : (
              <>
                {order.map((s, i) => (
                  <div key={s.name} className={classes.depItem}>
                    <span className={classes.depIndex}>{i + 1}.</span>
                    <span className={classes.serviceName}>{s.name}</span>
                  </div>
                ))}
                <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 8 }}>
                  Sequential · resolved from catalog dependsOn
                </Typography>
              </>
            )}
          </Paper>
        </Box>
      </Grid>
    </Grid>
  );
};

function EnvPath({
  sourceEnv,
  targetEnv,
  classes,
}: {
  sourceEnv: Env;
  targetEnv: Env;
  classes: ReturnType<typeof useStyles>;
}) {
  const envs: Env[] = ['dev', 'stage', 'prod'];
  return (
    <>
      {envs.map((e) => {
        const isSource = e === sourceEnv;
        const isTarget = e === targetEnv;
        const label = isSource ? 'Source' : isTarget ? 'Target' : 'Skipped';
        const active = isSource || isTarget;
        return (
          <div key={e} className={classes.envPathRow}>
            <div className={`${classes.envDot} ${active ? classes.envDotActive : ''}`} />
            <div style={{ flex: 1 }}>
              <div className={classes.envName}>{e}</div>
              <div className={classes.envSub}>{label}</div>
            </div>
          </div>
        );
      })}
    </>
  );
}
